import { useState, useEffect, useMemo } from "react";
import Editor from "@monaco-editor/react";
import "./HtmlPicker.css";

export default function HtmlPicker({ value, onChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const [htmlContent, setHtmlContent] = useState(value || "");

    useEffect(() => {
        setHtmlContent(value || "");
    }, [value]);

    const handleApply = () => {
        onChange(htmlContent);
        setIsOpen(false);
    };

    const handleCancel = () => {
        setHtmlContent(value || "");
        setIsOpen(false);
    };

    // Générer le HTML complet pour l'iframe de prévisualisation
    const previewHtml = useMemo(() => {
        if (!htmlContent) return '';
        
        // Si le HTML contient déjà <html> ou <body>, l'utiliser tel quel
        if (/<html[\s>]/i.test(htmlContent) || /<body[\s>]/i.test(htmlContent)) {
            return htmlContent;
        }

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: system-ui, sans-serif; }
                </style>
            </head>
            <body>
                ${htmlContent}
            </body>
            </html>`;
    }, [htmlContent]);

    // Stats pour le bouton
    const getStats = () => {
        const content = value || "";
        const lines = content.split('\n').length;
        const tags = (content.match(/<[^>]+>/g) || []).length;
        return { lines, tags };
    };

    const stats = getStats();

    return (
        <div className="html-picker-container">
            {/* Bouton principal */}
            <button 
                type="button"
                className="html-picker-trigger"
                onClick={() => setIsOpen(true)}
            >
                <span className="html-picker-icon">{"</>"}</span>
                <span className="html-picker-label">
                    {value ? `${stats.lines} lignes, ${stats.tags} balises` : "Éditer le HTML"}
                </span>
            </button>

            {/* Modal */}
            {isOpen && (
                <>
                    <div 
                        className="html-picker-overlay" 
                        onClick={handleCancel}
                    />
                    <div className="html-picker-modal">
                        {/* Header */}
                        <div className="html-picker-header">
                            <h3 className="html-picker-title">
                                <span className="html-picker-title-icon">{"</>"}</span>
                                Éditeur HTML
                            </h3>
                            <button 
                                type="button"
                                className="html-picker-close"
                                onClick={handleCancel}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Éditeur Monaco */}
                        <div className="html-picker-editor">
                            <Editor
                                height="100%"
                                defaultLanguage="html"
                                theme="vs-dark"
                                value={htmlContent}
                                onChange={(value) => setHtmlContent(value || "")}
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 14,
                                    lineNumbers: "on",
                                    wordWrap: "on",
                                    automaticLayout: true,
                                    scrollBeyondLastLine: false,
                                    padding: { top: 16, bottom: 16 },
                                    tabSize: 2,
                                    formatOnPaste: true,
                                    formatOnType: true,
                                    bracketPairColorization: { enabled: true },
                                    autoClosingBrackets: "always",
                                    autoClosingQuotes: "always",
                                }}
                            />
                        </div>

                        {/* Prévisualisation */}
                        <div className="html-picker-preview-section">
                            <div className="html-picker-preview-header">
                                <span>👁 Prévisualisation (avec JS)</span>
                            </div>
                            <iframe 
                                className="html-picker-preview"
                                srcDoc={previewHtml}
                                title="HTML Preview"
                                sandbox="allow-scripts allow-same-origin"
                            />
                        </div>

                        {/* Footer */}
                        <div className="html-picker-footer">
                            <button 
                                type="button"
                                className="html-picker-btn html-picker-btn-cancel"
                                onClick={handleCancel}
                            >
                                Annuler
                            </button>
                            <button 
                                type="button"
                                className="html-picker-btn html-picker-btn-apply"
                                onClick={handleApply}
                            >
                                Appliquer
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

